import { registeredTableHandlers } from '@/global/components/view-layouts/tabs-layout/config/registered-table-handlers'
import { registeredTabsHandlers } from '@/global/components/view-layouts/tabs-layout/common/registered-tabs-handlers'
import { dateService, FORMATS } from '@/global/services/date'
import { isArray } from '@/global/utils/utils'

const TAB_KEY = 'basicInfo'
const TYPE_PERSON_DOCUMENT = 'PERSON_DOCUMENTS'

export default {
  name: 'basicInfo',

  data () {
    return {
      typeDependantFields: {
        vehicles: {
          visible: false
        },
        people: {
          visible: false
        },
        receiveNotifications: {
          visible: false
        }
      },
      receiveNotificationsParams: {
        reminderTypeId: null
      },
      receiveNotificationsMethods: null
    }
  },

  computed: {
    notificationTypesItems () {
      return [
        {
          id: 'sms',
          name: this.$t('base.sms')
        },
        {
          id: 'email',
          name: this.$t('base.email')
        },
        {
          id: 'without_notifications',
          name: this.$t('satellite-tracking/alarm.without_notifications')
        }
      ]
    },

    basicInfoCreateConfig () {
      return {
        title: this.$t('base/profile.basic_info'),
        key: TAB_KEY,
        apiConfig: {
          post: {
            module: 'satellite-tracking',
            route: () => {
              return this.createMode ? 'reminders' : `reminders/${this.$route.params.id}`
            }
          },
          get: {
            module: 'satellite-tracking',
            route: `reminders/${this.$route.params.id}`,
            use: !this.createMode
          }
        },
        afterSave: (event) => this.afterSave(event),
        afterCancel: (event) => this.afterCancel(event),
        dataFetched: (event) => this.onDataFetched(event),
        modifyDataBeforeSend: (event) => this.onModifyDataBeforeSend(event),
        hiddenFormKeys: [
          'intervals'
        ],
        tabContent: [
          // back button, title and edit buttons
          {
            component: this.tabActionsComponent,
            title: this.createMode ? this.$t('satellite-tracking/reminders.adding_new_reminder') : this.$t('satellite-tracking/reminders.reminder_details'),
            showBackButton: {
              onGoBack: () => this.backToMainTable()
            },
            onCancelTabSave: (event) => this.onCancelSave(event)
          },
          // form data
          {
            component: this.columnsContentComponent,
            showButtons: false,
            rows: [
              {
                type: 'fields',
                columnsCount: 4,
                fields: [
                  // always visible fields
                  {
                    key: 'description',
                    label: this.$t('satellite-tracking/reminders.name'),
                    creatable: true,
                    editable: true,
                    visible: true
                  },
                  {
                    key: 'reminder_type_id',
                    label: this.$t('satellite-tracking/reminders.type'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      module: 'satellite-tracking',
                      route: 'reminder-types/autocomplete'
                    },
                    creatable: true,
                    editable: true,
                    visible: true,
                    onChange: (event) => this.onReminderTypeChange(event)
                  },
                  {
                    key: 'start_date',
                    label: this.$t('satellite-tracking/report.availability_status_report_duration_date_start'),
                    type: 'datetime-picker',
                    subType: 'date',
                    appendIcon: 'mdi-calendar-month',
                    format: FORMATS.date,
                    placeholder: FORMATS.date,
                    creatable: true,
                    editable: true,
                    visible: true
                  },
                  {
                    key: 'notification_types',
                    label: this.$t('satellite-tracking/alarm.notification_types'),
                    type: 'combobox',
                    autocomplete_options: {
                      fetchInitial: false
                    },
                    items: this.notificationTypesItems,
                    creatable: true,
                    editable: true,
                    visible: true
                  },
                  {
                    key: 'active_from',
                    label: this.$t('satellite-tracking/alarm.active_from'),
                    type: 'datetime-picker',
                    subType: 'time',
                    appendIcon: 'mdi-clock',
                    format: FORMATS.timeShort,
                    placeholder: FORMATS.timeShort,
                    creatable: true,
                    editable: true,
                    visible: true
                  },
                  {
                    key: 'active_to',
                    label: this.$t('satellite-tracking/alarm.active_to'),
                    type: 'datetime-picker',
                    subType: 'time',
                    appendIcon: 'mdi-clock',
                    format: FORMATS.timeShort,
                    placeholder: FORMATS.timeShort,
                    creatable: true,
                    editable: true,
                    visible: true
                  },
                  // reminder type dependant fields
                  {
                    key: 'vehicles',
                    label: this.$t('satellite-tracking/alarm.vehicles'),
                    type: 'combobox',
                    autocomplete_options: {
                      module: 'fleet',
                      route: 'vehicles/get-autocomplete-items-by-company'
                    },
                    creatable: true,
                    editable: true,
                    visible: this.typeDependantFields.vehicles.visible
                  },
                  {
                    key: 'people',
                    label: this.$t('satellite-tracking/reminders.people'),
                    type: 'combobox',
                    autocomplete_options: {
                      module: 'people',
                      route: 'people/autocomplete'
                    },
                    creatable: true,
                    editable: true,
                    visible: this.typeDependantFields.people.visible
                  },
                  {
                    key: 'receiving_notifications',
                    label: this.$t('fleet/alarm.receive_notifications'),
                    type: 'custom',
                    component: this.receiveNotificationsField,
                    reminderTypeId: this.receiveNotificationsParams.reminderTypeId,
                    methods: (event) => {
                      this.receiveNotificationsMethods = event?.event
                    },
                    creatable: true,
                    editable: true,
                    colSpan: 2,
                    visible: this.typeDependantFields.receiveNotifications.visible
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  methods: {
    onCancelSave (event) {
      // CREATE
      if (this.createMode) {
        this.createMode = false
      }
      // EDIT
      else {
        const { cancelTabSave } = event || {}
        cancelTabSave?.()
      }
    },

    afterCancel (event) {
      if (!this.createMode) {
        const { formDataForSend } = event || {}
        const { reminder_type: reminderType } = formDataForSend || {}
        const reminderTypeId = reminderType?.id || reminderType

        if (reminderTypeId) {
          this.handleDynamicFields(reminderTypeId)
          this.mapEditDynamicFieldsData(formDataForSend)
        }
      }
    },

    backToMainTable () {
      // CREATE
      if (this.createMode) {
        this.createMode = false
      }
      // EDIT
      else {
        this.$router.replace({ name: 'Reminders' }).then()
      }
    },

    afterSave (event) {
      // CREATE
      if (this.createMode) {
        this.createMode = false
        const { fetchData } = registeredTableHandlers?.reminders || {}
        fetchData?.()
      }
      // EDIT
      else {
        this.afterCancel(event)
      }
    },

    onModifyDataBeforeSend (event) {
      const { copiedData } = event || {}
      const { reminder_type_id: reminderTypeId } = copiedData || {}

      // skip if reminder type is not valid
      if (!reminderTypeId) {
        return
      }

      copiedData.reminder_type_id = reminderTypeId?.id || reminderTypeId
      copiedData.start_date = copiedData?.start_date ? dateService.formatCustom(copiedData.start_date, FORMATS.date, FORMATS.isoDate) : null
      // map notification types to array of strings (sms, email or without_notifications)
      if (copiedData?.notification_types?.length) {
        copiedData.notification_types = copiedData.notification_types.map(type => type?.id)?.filter(type => type)
      }

      if (reminderTypeId === TYPE_PERSON_DOCUMENT) {
        // map people to array of person ids
        if (copiedData?.people?.length) {
          copiedData.people = copiedData?.people?.map(person => person?.id)?.filter(person => person) || []
        }
        delete copiedData.vehicles
      }
      else {
        // map vehicles to array of vehicles ids
        if (copiedData?.vehicles?.length) {
          copiedData.vehicles = copiedData?.vehicles?.map(vehicle => vehicle?.id)?.filter(vehicle => vehicle) || []
        }
        delete copiedData.people
      }

      const intervalsWithoutUuid = this.receiveNotificationsMethods?.getIntervals?.()?.map(({ uuid, ...rest }) => rest)
      if (isArray(intervalsWithoutUuid) && intervalsWithoutUuid.length) {
        copiedData.intervals = intervalsWithoutUuid
      }
      else {
        delete copiedData.intervals
      }

      return copiedData
    },

    onReminderTypeChange (event) {
      const { selectedItem } = event || {}
      const reminderTypeId = selectedItem?.id || selectedItem
      // skip if reminder type id is not valid
      if (!reminderTypeId) {
        this.hideAllDynamicFields()
        this.$set(this.typeDependantFields.receiveNotifications, 'visible', false)
        this.$set(this.receiveNotificationsParams, 'reminderTypeId', null)
        return
      }

      this.handleDynamicFields(reminderTypeId)
    },

    handleDynamicFields (value) {
      // skip if value is not provided
      // value is reminder type id
      if (!value) {
        return
      }
      this.hideAllDynamicFields()

      if (value === TYPE_PERSON_DOCUMENT) {
        this.$set(this.typeDependantFields.people, 'visible', true)
        this.$set(this.typeDependantFields.vehicles, 'visible', false)
      }
      else {
        this.$set(this.typeDependantFields.vehicles, 'visible', true)
        this.$set(this.typeDependantFields.people, 'visible', false)
      }
      // show receive notifications
      this.$set(this.typeDependantFields.receiveNotifications, 'visible', true)
      this.$set(this.receiveNotificationsParams, 'reminderTypeId', value)
    },

    hideAllDynamicFields () {
      const hideFields = (fields) => {
        Object.keys(fields).forEach(key => {
          const field = fields[key]
          if (field && field.visible !== undefined) {
            this.$set(fields[key], 'visible', false)
          }
        })
      }

      hideFields(this.typeDependantFields)
    },

    onDataFetched (event) {
      const { data } = event?.data || {}
      const { reminder_type: reminderType } = data || {}
      const reminderTypeId = reminderType?.id || reminderType

      // skip if reminder type id is not valid
      if (!reminderTypeId) {
        return
      }

      this.handleDynamicFields(reminderTypeId)
      this.mapEditDynamicFieldsData(data)
    },

    mapEditDynamicFieldsData (data) {
      const {
        reminder_type: reminderType,
        vehicles_display: vehiclesDisplay,
        people_display: peopleDisplay,
        start_date: startDate
      } = data || {}
      const reminderTypeId = reminderType?.id || reminderType || null
      const { updateData } = registeredTabsHandlers?.basicInfo || {}

      // skip if type is not provided
      if (!reminderTypeId || typeof updateData !== 'function') {
        return
      }

      updateData({
        reminder_type_id: reminderTypeId,
        start_date: startDate ? dateService.formatCustom(startDate, FORMATS.isoDate, FORMATS.date) : null
      })

      if (reminderTypeId !== TYPE_PERSON_DOCUMENT) {
        updateData({
          vehicles: vehiclesDisplay?.map(vehicle => ({
            id: vehicle?.id || '--',
            name: vehicle?.registration_and_label || '--'
          }))
        })
      }
      else {
        updateData({
          people: peopleDisplay?.map(person => ({
            id: person?.id || '--',
            name: person?.full_name || '--'
          }))
        })
      }
    }
  }
}
